import React from 'react';
import { FieldPath } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { createEngageSender as createEngageSenderApi, getEngageDomains } from 'api/engage';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { EngageDomain } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface AddSenderFormValues {
    username: string;
    display_name: string;
    message_domain_id: string;
}

const AddSenderDrawer = () => {
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const history = useHistory();
    const { data: domains } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageDomains(accountId),
        queryFn: () => {
            return getEngageDomains(accountId);
        },
        select: (response): Array<EngageDomain> => {
            return response.data.data;
        },
    });

    const methods = Form.useGivebutterForm<AddSenderFormValues>({
        defaultValues: {
            username: '',
            display_name: '',
            message_domain_id: domains?.[0]?.id?.toString(),
        },
    });
    const { handleSubmit, getValues, setValue, control, setError, resetFormButton, path, watch } =
        methods;
    const { mutate: submit } = useMutation({
        mutationFn: () => {
            const formValues = getValues();
            return createEngageSenderApi(accountId, {
                ...formValues,
                message_domain_id: parseInt(formValues.message_domain_id),
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.engageSenders(accountId) });
            toast.success(<>Successfully added email sender</>);
            history.push(`/accounts/${accountId}/engage/settings/senders`);
        },
        onError: (error: GivebutterAxiosError) => {
            const errors = error.response?.data.errors;
            if (errors) {
                Object.keys(errors).forEach(key => {
                    setError(key as FieldPath<AddSenderFormValues>, {
                        message: errors[key][0],
                    });
                });
            } else {
                toast.error('Unknown error adding sender');
            }
            resetFormButton();
        },
    });

    const getUsernameFromName = (value: string) =>
        getUsernameFromGeneric(value).replace(/(.*)\.$/, '$1');

    const getUsernameFromGeneric = (value: string) =>
        value
            .toLowerCase()
            .replace(/\s\s*/g, '.')
            .replace(/\.{2,}$/g, '.')
            .replace(/-{2,}(?!-)$/g, '-')
            .replace(/_{2,}(?!_)$/g, '_')
            .replace(/[^a-zA-Z0-9\s._-]/g, '');

    const onChangeUsername = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue('username', getUsernameFromGeneric(e.target.value));
    };

    const { display_name, username } = watch();
    const onChangeDisplayName = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        if (username.length === 0 || username === getUsernameFromName(display_name)) {
            setValue('username', getUsernameFromName(value));
        }

        setValue('display_name', value);
    };

    return (
        <Form.Provider {...methods}>
            <form className={'contents'} onSubmit={handleSubmit(() => submit())}>
                <Drawer.Header>
                    <Drawer.Title>Add email sender</Drawer.Title>
                </Drawer.Header>
                <Drawer.Body>
                    <div className="flex flex-col gap-2">
                        <Form.Container>
                            <Form.Label name={path('display_name')}>Sender name</Form.Label>
                            <Form.TextInputRoot control={control} name="display_name">
                                <Form.TextInputForm
                                    placeholder="Jane Smith"
                                    onChange={onChangeDisplayName}
                                />
                            </Form.TextInputRoot>
                            <Form.Error name={path('display_name')} />
                        </Form.Container>
                        <Form.Container>
                            <Form.Label name={path('username')}>Sender email</Form.Label>
                            <div className="mt-1.5 flex w-full">
                                <Form.TextInputRoot
                                    control={control}
                                    name="username"
                                    inputRootProps={{
                                        className: 'input-left',
                                    }}
                                >
                                    <Form.TextInputForm
                                        placeholder="jane.smith"
                                        onChange={onChangeUsername}
                                    />
                                </Form.TextInputRoot>
                                <Form.SingleSelect control={control} name="message_domain_id">
                                    <SingleSelect.Trigger
                                        aria-label="Domain"
                                        className="basis-min-content rounded-l-none"
                                    />
                                    <SingleSelect.Content>
                                        {domains.map(domain => (
                                            <SingleSelect.Item
                                                value={domain.id.toString()}
                                                key={domain.id}
                                            >
                                                <SingleSelect.ItemText>
                                                    @{domain.value}
                                                </SingleSelect.ItemText>
                                            </SingleSelect.Item>
                                        ))}
                                    </SingleSelect.Content>
                                </Form.SingleSelect>
                            </div>
                            <Form.Error name={path('username')} />
                        </Form.Container>
                    </div>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel asChild={true}>
                            <Button variant="transparent">Cancel</Button>
                        </Drawer.Cancel>

                        <Form.SubmitButton>Add sender</Form.SubmitButton>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

export { AddSenderDrawer };
